'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { api } from '../api/client';
import type { AppConfig } from '../api/types';

const FALLBACK_CONFIG: AppConfig = {
  default_interval_seconds: 1800,
  min_interval_seconds: 1800,
  max_loss_usd: 500,
};

type ConfigContextType = {
  config: AppConfig;
  configLoaded: boolean;
};

const ConfigContext = createContext<ConfigContextType>({
  config: FALLBACK_CONFIG,
  configLoaded: false,
});

export function ConfigProvider({ children }: PropsWithChildren) {
  const [config, setConfig] = useState<AppConfig>(FALLBACK_CONFIG);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    api
      .getConfig()
      .then((data) => setConfig(data))
      .catch(() => {
        // Endpoint may not exist yet — keep fallback defaults.
      })
      .finally(() => setConfigLoaded(true));
  }, []);

  return (
    <ConfigContext.Provider value={{ config, configLoaded }}>{children}</ConfigContext.Provider>
  );
}

export function useConfig() {
  return useContext(ConfigContext);
}
